// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import * as globals from '@wandb/weave/common/css/globals.styles';
import {clsx} from 'clsx';
import React from 'react';

import {useSetHighlight} from '../../state/interactState/hooks';
import {getLegendOverrideKey} from '../../util/plotHelpers/legend';
import {markToIconNameMap} from '../LineStylePicker';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode';
import FancyLegend from '../vis/FancyLegend';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {ColorPaletteOption, getTextColor} from './../../util/colors';
import {
  type PlotFontSize,
  plotFontSizeToPx,
} from './../../util/plotHelpers/plotFontSize';
import {type Line} from './../../util/plotHelpers/types';
import {usePanelConfigContext} from './PanelConfigContext';

type LinePlotLegendProps = {
  children?: React.ReactNode;
  disableRunLinks?: boolean;
  expandLegend?: boolean;
  fontSize: PlotFontSize;
  hiddenLines: Line[];
  isVertical: boolean;
  legendPrefix?: React.Component | JSX.Element;
  lines: Line[];
  disallowSelect: boolean;
};
export const LinePlotLegend = React.memo(
  ({
    disableRunLinks,
    disallowSelect,
    fontSize,
    hiddenLines,
    isVertical,
    lines,
  }: LinePlotLegendProps) => {
    const {isSingleRun} = useSingleRunModeContext();
    const setHighlightRun = useSetHighlight();
    const fSize = plotFontSizeToPx.legend[fontSize];

    const wrapperClasses = clsx(
      `scrollbar-hide flex flex-shrink-0 flex-wrap gap-x-8 gap-y-2 leading-none`,
      {
        // tailwind requires the classes to be fully built for static analysis
        // https://tailwindcss.com/docs/content-configuration#dynamic-class-names
        'text-[10px]': fSize === 10,
        'text-[12px]': fSize === 12,
        'text-[14px]': fSize === 14,
        'flex-column w-full items-center justify-center': isVertical,
        'flex-row justify-start': !isVertical,
        'select-none': disallowSelect,
      }
    );

    const hiddenLineIds = React.useMemo(
      () => new Set(hiddenLines.map(l => getLegendOverrideKey(l))),
      [hiddenLines]
    );

    const colorPaletteOption = useWorkspaceSettingsColorPalette();
    const {colorRunNames} = usePanelConfigContext();

    return (
      <div className={wrapperClasses} data-test="line-plot-legend">
        {lines.map((line, i) => {
          const hidden = hiddenLineIds.has(getLegendOverrideKey(line));
          const color = !hidden ? line.color : globals.gray500;

          return (
            <div
              className={`flex items-center ${
                isVertical ? '' : 'flex-column basis-full'
              }`}
              key={`${line.uniqueId}-${i}`}>
              <span
                className="line-plot-color night-aware flex items-center"
                style={{color}}>
                <LegacyWBIcon name={markToIconNameMap[line.mark ?? 'solid']} />
              </span>
              <span
                data-test="line-plot-legend-run-name"
                className="night-aware whitespace-nowrap text-moon-650 dark:text-moon-350"
                onMouseOver={() => {
                  if (!isSingleRun) {
                    setHighlightRun('run:name', line.uniqueId);
                  }
                }}
                onMouseOut={() => {
                  if (!isSingleRun) {
                    setHighlightRun('run:name', undefined);
                  }
                }}
                style={{
                  color:
                    colorPaletteOption === ColorPaletteOption.DEFAULT &&
                    colorRunNames
                      ? getTextColor(color)
                      : undefined,
                }}>
                {line.fancyTitle &&
                  FancyLegend({
                    ...line.fancyTitle,
                    disableRunLink: disableRunLinks,
                  })}
              </span>
            </div>
          );
        })}
      </div>
    );
  }
);

LinePlotLegend.displayName = 'LinePlotLegend';
